import {
  BudgetItemTypeEnum,
  BudgetItemType,
  CalcOption,
  ItemTypeList,
  ItemTypeListPerCalcOption,
} from "../types/budget/budget";

const CALCS_FOR_CATEGORY_SYNC = ["A", "B", "C", "D"] as const;

/**
 * Consolida categorias marcadas como habilitadas (S) na etapa Serviços.
 * Considera qualquer forma de pagamento A–D: se a categoria estiver true em uma delas, entra no conjunto habilitado.
 */
export function mergeEnabledCategoryKeysFromServiceItemTypeList(
  itemTypeList: Partial<ItemTypeListPerCalcOption> | undefined | null
): Record<string, boolean> {
  const enabled: Record<string, boolean> = {};
  if (!itemTypeList) return enabled;
  for (const calc of CALCS_FOR_CATEGORY_SYNC) {
    const slice = itemTypeList[calc] as
      | Record<string, boolean | undefined>
      | undefined;
    if (!slice) continue;
    for (const [key, value] of Object.entries(slice)) {
      if (value === true) enabled[key] = true;
    }
  }
  return enabled;
}

/**
 * Monta itemTypeList do orçamento: mesmas flags S/N em A, B, C e D (categorias vindas de Serviços).
 */
export function buildItemTypeListReplicatingServiceCategoriesToAllCalcs(
  enabledByCategoryKey: Record<string, boolean>
): ItemTypeListPerCalcOption {
  const row = {} as ItemTypeList;
  (Object.keys(BudgetItemTypeEnum) as BudgetItemType[]).forEach((key) => {
    (row as any)[key] = !!enabledByCategoryKey[key];
  });
  return {
    A: { ...row },
    B: { ...row },
    C: { ...row },
    D: { ...row },
    budgetTotals: {},
  };
}
import { CategoryInfo } from "../types/budget/import-services";
import { BudgetIcons } from "../assets/styled-icons/budget";

type ActiveServiceLike = {
  addedItemTypeList?: Array<{ type: BudgetItemType }>;
} | null;

/**
 * Identifica categorias que possuem fornecedores/serviços cadastrados
 * Verifica em addedItemTypeList para encontrar categorias com dados
 */
export function getCategoriesWithServices(
  activeService: ActiveServiceLike
): CategoryInfo[] {
  if (!activeService?.addedItemTypeList) return [];

  // Extrair categorias únicas do addedItemTypeList
  const uniqueCategories = Array.from(
    new Set(activeService.addedItemTypeList.map((item) => item.type))
  );

  // Mapear para CategoryInfo com label e ícone
  return uniqueCategories.map((categoryKey) => ({
    key: categoryKey,
    label: BudgetItemTypeEnum[categoryKey],
    icon: BudgetIcons[categoryKey],
  }));
}

/**
 * Constrói itemTypeList onde cada categoria fica true só na forma indicada (demais false).
 * Usado quando se precisa desse layout; na importação Serviços → Orçamento as flags S/N
 * vêm de mergeEnabledCategoryKeysFromServiceItemTypeList + buildItemTypeListReplicatingServiceCategoriesToAllCalcs.
 */
export function buildItemTypeListFromMapping(
  categoryMapping: Map<string, CalcOption>
): ItemTypeListPerCalcOption {
  const itemTypeList: ItemTypeListPerCalcOption = {
    A: {},
    B: {},
    C: {},
    D: {},
    budgetTotals: {},
  };

  // Para cada categoria mapeada, alocar apenas na forma escolhida
  categoryMapping.forEach((calcOption, categoryKey) => {
    (itemTypeList[calcOption] as any)[categoryKey] = true;
  });

  // Categorias não mapeadas ficam false em todas as formas
  const allCategories = Object.keys(BudgetItemTypeEnum) as BudgetItemType[];
  allCategories.forEach((cat) => {
    ["A", "B", "C", "D"].forEach((option) => {
      if (!(cat in itemTypeList[option as CalcOption])) {
        (itemTypeList[option as CalcOption] as any)[cat] = false;
      }
    });
  });

  return itemTypeList;
}

/**
 * Filtra serviços baseado no mapeamento de categorias
 * Mantém apenas serviços das categorias e formas de pagamento escolhidas
 */
export function filterServicesByMapping(
  services: any[],
  mapping: Map<string, CalcOption>
) {
  return services.filter((service) => {
    const mappedCalcOption = mapping.get(service.type);
    return mappedCalcOption && mappedCalcOption === service.calcOption;
  });
}

/**
 * Filtra addedItemTypeList baseado no mapeamento de categorias
 * Mantém apenas itens das categorias e formas de pagamento escolhidas
 */
export function filterAddedItemsByMapping(
  items: any[],
  mapping: Map<string, CalcOption>
) {
  return items.filter((item) => {
    const mappedCalcOption = mapping.get(item.type);
    return mappedCalcOption && mappedCalcOption === item.calcOption;
  });
}

/** Mantém a ordem da planilha do Estudo de Verba na importação para o orçamento. */
export function withDraftSortOrder<T>(services: T[] | undefined): T[] {
  return (services || []).map((service, index) => ({
    ...service,
    sortOrder: index,
  }));
}

export function applyCategoryCalcOptionMapping(
  serviceLikeData: any,
  mapping: Map<string, CalcOption>
): any {
  if (!serviceLikeData || mapping.size === 0) return serviceLikeData;

  const remap = (type: any, current: any) => {
    const next = mapping.get(String(type));
    return next || current;
  };

  if (Array.isArray(serviceLikeData.budgetServices)) {
    serviceLikeData.budgetServices = serviceLikeData.budgetServices.map(
      (s: any) => ({
        ...s,
        calcOption: remap(s.type, s.calcOption),
      })
    );
  }

  if (Array.isArray(serviceLikeData.addedItemTypeList)) {
    serviceLikeData.addedItemTypeList = serviceLikeData.addedItemTypeList.map(
      (i: any) => ({
        ...i,
        calcOption: remap(i.type, i.calcOption),
      })
    );
  }

  if (Array.isArray(serviceLikeData.baseValues)) {
    serviceLikeData.baseValues = serviceLikeData.baseValues.map((b: any) => ({
      ...b,
      calcOption: remap(b.category, b.calcOption),
    }));
  }

  if (Array.isArray(serviceLikeData.mainAppliedTaxes)) {
    serviceLikeData.mainAppliedTaxes = serviceLikeData.mainAppliedTaxes.map(
      (t: any) => ({
        ...t,
        calcOption: remap(t.category, t.calcOption),
      })
    );
  }

  if (Array.isArray(serviceLikeData.files)) {
    serviceLikeData.files = serviceLikeData.files.map((f: any) => ({
      ...f,
      calcOption: remap(f.type, f.calcOption),
    }));
  }

  if (Array.isArray(serviceLikeData.paymentSolicitation)) {
    serviceLikeData.paymentSolicitation =
      serviceLikeData.paymentSolicitation.map((p: any) => ({
        ...p,
        calcOption: remap(p.type, p.calcOption),
      }));
  }

  if (Array.isArray(serviceLikeData.supplierList)) {
    const supplierKeySet = new Set<string>();
    const nextSupplierList: any[] = [];

    const candidates = Array.isArray(serviceLikeData.addedItemTypeList)
      ? serviceLikeData.addedItemTypeList
      : [];

    candidates.forEach((i: any) => {
      const calcOption = remap(i.type, i.calcOption);
      const key = `${i.supplierId}__${calcOption}`;
      if (supplierKeySet.has(key)) return;
      supplierKeySet.add(key);
      nextSupplierList.push({ supplierId: i.supplierId, calcOption });
    });

    serviceLikeData.supplierList = nextSupplierList;
  }

  return serviceLikeData;
}
